import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetPasswordSchema } from '../schemas/resetPasswordSchema';
import { resetPassword } from '../api/user';
import { useNavigate } from 'react-router-dom';
import LabeledTextField from '../components/LabeledTextField';
import UserPageLayout from '../components/UserPageLayout';
import { Box, Button, Alert, Typography, Stack } from '@mui/material';
import { NoticeModal } from '../components/Modals';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState('');

  const showNotice = (msg) => {
    setNoticeMsg(msg);
    setNoticeOpen(true);
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
  } = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data) => {
    try {
      await resetPassword(data.password, data.confirmPassword);
      showNotice('パスワードが変更されました。');
      navigate('/mypage');
    } catch (err) {
      setFormError('root', {
        type: 'manual',
        message: 'パスワードのリセットに失敗しました',
      });
      console.error('Reset Password Error:', err);
    }
  };

  return (
    <UserPageLayout title="パスワード再設定" maxWidth="sm">
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          maxWidth: 'none',
          boxShadow: 'none',
          minHeight: 'fit-content',
          padding: 0,
          mt: '5dvh',
          alignItems: 'flex-start',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            mb: 3,
            color: 'text.secondary',
            textAlign: 'center',
            lineHeight: 1.6,
          }}
        >
          パスワードは英数字を組み合わせた
          <br />
          8文字以上の文字列にしてください
        </Typography>

        <LabeledTextField
          label="新しいパスワード"
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          autoFocus
          placeholder="新しいパスワードを入力してください"
          register={register}
          error={errors.password}
        />

        <LabeledTextField
          label="新しいパスワード（確認）"
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          placeholder="パスワードを再入力してください"
          register={register}
          error={errors.confirmPassword}
        />

        {errors.root && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.root.message}
          </Alert>
        )}

        <Stack direction="column" spacing={2} sx={{ mt: 3, width: '100%' }}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ borderRadius: 5 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? '変更中...' : 'パスワードを変更'}
          </Button>

          <Button
            variant="outlined"
            fullWidth
            color="primary"
            onClick={() => navigate('/mypage')}
            sx={{ borderRadius: 5 }}
            disabled={isSubmitting}
          >
            戻る
          </Button>
        </Stack>
      </Box>
      <NoticeModal
        open={noticeOpen}
        message={noticeMsg}
        onClose={() => setNoticeOpen(false)}
      />
    </UserPageLayout>
  );
};

export default ResetPassword;
